import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { wardrobeApi } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { ArrowRight, Leaf } from 'lucide-react';

export default function SharedOutfitPage() {
  const { token } = useParams();
  const { t, tField } = useLang();
  const [outfit, setOutfit] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    wardrobeApi.getSharedOutfit(token)
      .then((r) => setOutfit(r.data))
      .catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen pt-40 text-center px-6" data-testid="shared-outfit-notfound">
        <p className="text-2xl font-light mb-4">{t({ it: 'Outfit non trovato', en: 'Outfit not found' })}</p>
        <Link to="/shop" className="btn-primary inline-block">{t({ it: 'Scopri la collezione', en: 'Discover the collection' })}</Link>
      </div>
    );
  }

  if (!outfit) {
    return (
      <div className="min-h-screen pt-40 px-6 md:px-24">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-[#E5E0D8] w-72 mx-auto" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-72 bg-[#E5E0D8]" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="shared-outfit-page" className="min-h-screen bg-[#FAF9F6] pt-24 pb-24">
      <section className="px-6 md:px-12 lg:px-24 py-12 text-center">
        <p className="overline mb-4 flex items-center justify-center gap-2">
          <Leaf className="w-4 h-4 text-[#4A5D23]" />
          {outfit.owner_name
            ? t({ it: `L'outfit di ${outfit.owner_name}`, en: `${outfit.owner_name}'s outfit` })
            : t({ it: 'Un outfit biolosophy', en: 'A biolosophy outfit' })}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1917] mb-4" data-testid="shared-outfit-name">
          {outfit.name}
        </h1>
        <p className="text-sm text-[#57534E] max-w-md mx-auto">
          {t({
            it: 'Capi in cotone e fibre biologiche, interamente realizzati in Italia.',
            en: 'Garments in organic cotton and fibres, entirely made in Italy.',
          })}
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24">
        <div className="flex flex-wrap justify-center gap-6">
          {outfit.items.map((item, idx) => (
            <Link
              key={item.product_id}
              to={`/product/${item.product_id}`}
              data-testid={`shared-outfit-item-${idx}`}
              className="group w-64 bg-white border border-[#E5E0D8] hover:border-[#1C1917] transition-colors"
            >
              <div className="aspect-[3/4] overflow-hidden bg-[#E5E0D8]">
                {item.image && <img src={item.image} alt={tField(item, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              </div>
              <div className="p-4">
                <p className="font-medium text-sm">{tField(item, 'name')}</p>
                {item.available ? (
                  <p className="text-sm text-[#4A5D23] font-medium mt-1">
                    {item.price?.toFixed(2)}€ <ArrowRight className="w-3.5 h-3.5 inline" />
                  </p>
                ) : (
                  <p className="text-xs text-[#A8A29E] mt-1">{t({ it: 'Non disponibile', en: 'Unavailable' })}</p>
                )}
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-16">
          <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="shared-outfit-cta">
            {t({ it: 'Scopri tutta la collezione biolosophy', en: 'Discover the full biolosophy collection' })}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
